import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import useMenus from '../../context/MenuContext';
import { sxConfig } from './masterdataClassification.style';
import {
  MasterDataClassificationFieldList,
  MasterDataClassificationTypeList,
} from '../../config/masterdataClassification';
import { FormLabelUI, SelectUI } from '../Interface';
import masterdataClassificationService from '../../service/masterdataClassification.service';
import { NotificationType } from '../../config';
import addNotification from '../../utils/addNotification';

export const MasterdataClassificationPage = () => {
  const { menus, getMenus, getSubMenuNamesByUrls } = useMenus();
  const [items, setItems] = useState([]);
  const [type, setType] = useState('');
  const [editingRowId, setEditingRowId] = useState(null);
  const [editedName, setEditedName] = useState('');
  const editingRef = useRef(null);

  useEffect(() => {
    if (!type) return;
    getMasterdataClassification();
  }, [type]);

  useEffect(() => {
    getMenus();
  }, []);

  useEffect(() => {
    const handleClickOutside = async (event) => {
      if (editingRef.current && !editingRef.current.contains(event.target)) {
        await saveEditingRow();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingRowId, editedName]);

  const submenuNames = useMemo(() => {
    return getSubMenuNamesByUrls(['resourceOne']);
  }, [menus]);

  const getMasterdataClassification = async () => {
    const names = await masterdataClassificationService.getClassificationByType(type);

    const updatedFields = MasterDataClassificationFieldList.map((field) => {
      const foundField = names.find((n) => n.field === field.value);
      return {
        ...field,
        label: foundField?.name ?? field.label,
      };
    });

    setItems(updatedFields);
  };

  const getOriginalLabel = (field) => {
    const original = items.find((item) => item.value === field);
    return original?.label;
  };

  const saveEditingRow = async () => {
    if (editingRowId !== null) {
      if (!editedName.trim()) {
        addNotification({
          content: 'A mező nem lehet üres!',
          type: NotificationType.ERROR,
        });
        return false;
      }
      if (editedName !== getOriginalLabel(editingRowId)) {
        await handleEditSave(editingRowId);
      }

      setEditingRowId(null);
      setEditedName('');
    }
    return true;
  };

  const handleEditSave = async (field) => {
    const current = items.find((item) => item.value === field);
    if (!current) return;

    const payload = {
      type,
      field: current.value,
      name: editedName,
    };

    await masterdataClassificationService.upsertMasterdataClassification(payload).then(() => {
      setItems((prev) => prev.map((item) => (item.value === field ? { ...item, label: editedName } : item)));
    });
  };

  const handleRowClick = async (field) => {
    if (editingRowId === field) return;

    const success = await saveEditingRow();
    if (!success) return;

    const current = items.find((item) => item.value === field);
    if (current) {
      setEditedName(current.label);
      setEditingRowId(field);
    }
  };

  return (
    <Box sx={sxConfig.pageParentBox}>
      <Box sx={sxConfig.childrenParentBox}>
        <Box sx={sxConfig.selectBox}>
          <SelectUI
            option={MasterDataClassificationTypeList(submenuNames)}
            label={<FormLabelUI text="Felület" />}
            onChange={(e) => {
              const value = e.target.value;
              setType(value);
            }}
          />
        </Box>

        <TableContainer component={Paper} sx={sxConfig.tableContainer}>
          <Table>
            <TableHead sx={sxConfig.tableHead}>
              <TableRow>
                <TableCell>Besorolás megnevezése</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {items.map((item, index) => (
                <TableRow
                  key={item.value}
                  onClick={() => handleRowClick(item.value)}
                  ref={editingRowId === item.value ? editingRef : null}
                  sx={index % 2 === 0 ? sxConfig.tableRowBgGray : sxConfig.tableRowBgWhite}
                >
                  <TableCell sx={sxConfig.tableCell}>
                    {editingRowId === item.value ? (
                      <TextField
                        variant="standard"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        fullWidth
                        autoFocus
                      />
                    ) : (
                      item.label
                    )}
                  </TableCell>
                  <TableCell sx={sxConfig.tableCell}>
                    {editingRowId === item.value && (
                      <IconButton
                        size="small"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await saveEditingRow();
                        }}
                      >
                        <CheckIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};
